import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { ChipModule } from 'primeng/chip';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';

import { FormFieldErrorComponent } from '@tourfold/libraries/shared-ui';
import type { ServerFieldErrors } from '@tourfold/libraries/shared-util';

import { OptionWithInfoComponent } from './option-with-info.component';

export type UserFormMode = 'create' | 'edit';

export interface RoleOption {
  label: string;
  value: string;
  description: string;
}

export interface SkillOption {
  label: string;
  value: string;
}

export interface TagOption {
  label: string;
  value: string;
  color: string;
}

@Component({
  selector: 'tf-user-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    InputTextModule,
    MultiSelectModule,
    ChipModule,
    FormFieldErrorComponent,
    OptionWithInfoComponent,
  ],
  templateUrl: './user-form.component.html',
  styleUrl: './user-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserFormComponent {
  readonly formGroup = input.required<FormGroup>();
  readonly mode = input.required<UserFormMode>();
  readonly idPrefix = input.required<string>();
  readonly serverErrors = input.required<ServerFieldErrors>();
  readonly availableRoles = input.required<RoleOption[]>();
  readonly availableSkills = input.required<SkillOption[]>();
  readonly availableTagOptions = input.required<TagOption[]>();
  readonly canAssignSkills = input.required<boolean>();
  readonly getRoleLabel = input.required<(roleId: string) => string>();
  readonly canAssignRole = input.required<(roleId: string) => boolean>();

  readonly isCreate = computed(() => this.mode() === 'create');
  readonly isEdit = computed(() => this.mode() === 'edit');

  hasAnyRoles(): boolean {
    return ((this.formGroup().get('roles')?.value as string[] | null) ?? []).length > 0;
  }

  errorMessage(field: string): string | undefined {
    return this.serverErrors().value()[field];
  }

  errorId(field: string): string {
    return `${this.idPrefix()}-${field}-error`;
  }

  fieldId(field: string): string {
    return `${this.idPrefix()}-${field}`;
  }

  /**
   * Edit-mode role changes preserve any non-assignable role ids the user already has — those
   * roles are visible in the chip list but must not be removable through the picker.
   */
  onRolesChange(values: string[] | null | undefined): void {
    const control = this.formGroup().get('roles');
    if (!control) {
      return;
    }
    const currentValues = (control.value ?? []) as string[];
    const nonAssignable = currentValues.filter((roleId) => !this.canAssignRole()(roleId));
    const merged = Array.from(new Set([...(values ?? []), ...nonAssignable]));
    control.setValue(merged, { emitEvent: false });
  }

  onRolesClear(): void {
    const control = this.formGroup().get('roles');
    if (!control) {
      return;
    }
    const currentValues = (control.value ?? []) as string[];
    const nonAssignable = currentValues.filter((roleId) => !this.canAssignRole()(roleId));
    control.setValue(nonAssignable, { emitEvent: false });
  }

  removeRole(roleId: string): void {
    if (!this.canAssignRole()(roleId)) {
      return;
    }
    const control = this.formGroup().get('roles');
    if (!control) {
      return;
    }
    const next = ((control.value ?? []) as string[]).filter((value) => value !== roleId);
    control.setValue(next, { emitEvent: false });
  }
}
